import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Divider,
  FormControlLabel,
  Checkbox,
  Paper,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import WatchIcon from '@mui/icons-material/Watch';
import DiamondIcon from '@mui/icons-material/Diamond';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { AuthContext } from '../../contexts/AuthContext';
import { db, storage } from '../../firebase';
import { collection, addDoc, updateDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { addLog } from '../../services/logs';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import heic2any from 'heic2any';
import {
  locationMap,
  watchMaterialMap,
  jewelryMaterialMap,
  braceletMap,
  diamondsMap,
  conditionMap,
  colorMap,
  documentsMap,
  jewelryTypeMap,
  goldColorMap,
  goldFinenessMap,
  stoneShapeMap,
  settingTypeMap,
  claspTypeMap,
  statusMap,
  categoryMap,
  collectionMap
} from '../../utils/productMappings';

// Stałe
const WATCH_BRANDS_MODELS_BRACELETS = {
  "Rolex": {
    "Oyster Perpetual": {
      "diameters": [28, 31, 34, 36, 39, 41],
      "bracelets": ["Oyster"]
    },
    "Datejust": {
      "diameters": [26, 28, 31, 36, 41],
      "bracelets": ["Oyster", "Jubilee"]
    },
    "Day-Date": {
      "diameters": [36, 40],
      "bracelets": ["President"]
    },
    "Submariner": {
      "diameters": [40, 41],
      "bracelets": ["Oyster"]
    },
    "GMT-Master II": {
      "diameters": [40],
      "bracelets": ["Oyster", "Jubilee"]
    },
    "Explorer": {
      "diameters": [36, 39, 42],
      "bracelets": ["Oyster"]
    },
    "Yacht-Master": {
      "diameters": [37, 40, 42],
      "bracelets": ["Oyster", "Oysterflex"]
    },
    "Sky-Dweller": {
      "diameters": [42],
      "bracelets": ["Oyster", "Jubilee"]
    },
    "Cosmograph Daytona": {
      "diameters": [40],
      "bracelets": ["Oyster", "Oysterflex"]
    },
    "Cellini": {
      "diameters": [39],
      "bracelets": ["Leather"]
    },
    "1908": {
      "diameters": [39],
      "bracelets": ["Leather"]
    },
    "Sea-Dweller": {
      "diameters": [43],
      "bracelets": ["Oyster"]
    },
    "Land-Dweller": {
      "diameters": [42],
      "bracelets": ["Oyster"]
    }
  },
  "Chopard": {
    "Happy Sport": {
      "diameters": [30, 36, 40],
      "bracelets": ["Rubber", "Leather"]
    },
    "Mille Miglia": {
      "diameters": [39, 42, 44],
      "bracelets": ["Rubber", "Leather"]
    },
    "L.U.C": {
      "diameters": [39, 40, 42],
      "bracelets": ["Leather", "Alligator"]
    },
    "Alpine Eagle": {
      "diameters": [36, 41, 44],
      "bracelets": ["Leather", "Pin Buckle"]
    },
    "Happy Diamonds": {
      "diameters": [30, 32, 36],
      "bracelets": ["Leather", "Metal"]
    },
    "Impériale": {
      "diameters": [29, 36, 40],
      "bracelets": ["Leather", "Metal"]
    },
    "L'Heure du Diamant": {
      "diameters": [30, 35, 40],
      "bracelets": ["Leather", "Metal"]
    }
  },
  "Cartier": {
    "Tank Louis": {
      "diameters": [25, 29, 33],
      "bracelets": ["Leather", "Metal"]
    },
    "Tank Must": {
      "diameters": [29, 34],
      "bracelets": ["Leather", "Metal"]
    },
    "Tank Française": {
      "diameters": [25, 30, 37],
      "bracelets": ["Leather", "Metal"]
    },
    "Tank Américaine": {
      "diameters": [19, 22, 27],
      "bracelets": ["Leather", "Metal"]
    },
    "Tank Anglaise": {
      "diameters": [26, 30, 39],
      "bracelets": ["Leather", "Metal"]
    },
    "Tank MC": {
      "diameters": [34, 39, 44],
      "bracelets": ["Leather", "Metal"]
    },
    "Tank Solo": {
      "diameters": [24, 31, 35],
      "bracelets": ["Leather", "Metal"]
    },
    "Santos de Cartier": {
      "diameters": [35, 40, 48],
      "bracelets": ["Leather", "Metal"]
    },
    "Santos Demoiselle": {
      "diameters": [20, 26],
      "bracelets": ["Leather", "Metal"]
    },
    "Santos-Dumont": {
      "diameters": [27, 31, 38, 43],
      "bracelets": ["Leather", "Metal"]
    },
    "Panthère de Cartier": {
      "diameters": [22, 27, 30],
      "bracelets": ["Metal"]
    },
    "Ballon Bleu de Cartier": {
      "diameters": [28, 33, 36, 40, 42],
      "bracelets": ["Leather", "Metal"]
    },
    "Pasha de Cartier": {
      "diameters": [35, 41, 42],
      "bracelets": ["Leather", "Metal"]
    },
    "Baignoire": {
      "diameters": [24, 31, 35],
      "bracelets": ["Leather", "Metal"]
    },
    "Ronde de Cartier": {
      "diameters": [29, 36, 40, 42],
      "bracelets": ["Leather", "Metal"]
    },
    "Cartier Libre": {
      "diameters": [30, 35, 40],
      "bracelets": ["Leather", "Metal"]
    }
  },
  "Audemars Piguet": {
    "Royal Oak Self-winding": {
      "diameters": [33, 34, 37, 39, 41],
      "bracelets": ["Metal", "Leather"]
    },
    "Royal Oak Chronograph": {
      "diameters": [38, 41],
      "bracelets": ["Metal", "Leather", "Rubber"]
    },
    "Royal Oak Perpetual Calendar": {
      "diameters": [41],
      "bracelets": ["Metal", "Leather"]
    },
    "Royal Oak Ultra Thin": {
      "diameters": [39, 41],
      "bracelets": ["Metal"]
    },
    "Royal Oak Tourbillon": {
      "diameters": [41],
      "bracelets": ["Metal", "Leather"]
    },
    "Royal Oak Double Balance": {
      "diameters": [41, 43],
      "bracelets": ["Metal", "Leather"]
    },
    "Royal Oak Offshore": {
      "diameters": [42, 44],
      "bracelets": ["Metal", "Rubber"]
    },
    "Royal Oak Concept": {
      "diameters": [44],
      "bracelets": ["Metal", "Rubber"]
    },
    "[Re]Master": {
      "diameters": [40],
      "bracelets": ["Leather"]
    },
    "Code 11.59": {
      "diameters": [38, 41],
      "bracelets": ["Leather", "Metal"]
    },
    "Millenary": {
      "diameters": [39, 45],
      "bracelets": ["Leather", "Metal"]
    },
    "Jules Audemars": {
      "diameters": [39, 41],
      "bracelets": ["Leather"]
    },
    "Haute Joaillerie": {
      "diameters": [37, 39],
      "bracelets": ["Metal", "Leather"]
    }
  },
  "Patek Philippe": {
    "Nautilus": {
      "diameters": [35, 40, 42],
      "bracelets": ["Metal", "Leather"]
    },
    "Aquanaut": {
      "diameters": [36, 39, 42],
      "bracelets": ["Metal", "Leather", "Rubber"]
    },
    "Calatrava": {
      "diameters": [36, 37, 38, 39],
      "bracelets": ["Leather", "Metal"]
    },
    "Golden Ellipse": {
      "diameters": [34, 39],
      "bracelets": ["Leather"]
    },
    "Gondolo": {
      "diameters": [30, 32, 36, 38, 40],
      "bracelets": ["Leather", "Metal"]
    },
    "Twenty-4": {
      "diameters": [25, 36],
      "bracelets": ["Metal"]
    },
    "Complications World Time": {
      "diameters": [38, 40],
      "bracelets": ["Leather", "Metal"]
    },
    "Complications Annual Calendar": {
      "diameters": [38, 40, 42],
      "bracelets": ["Leather", "Metal"]
    },
    "Complications Moon Phases": {
      "diameters": [37, 39],
      "bracelets": ["Leather"]
    },
    "Grand Complications Celestial": {
      "diameters": [44],
      "bracelets": ["Leather"]
    },
    "Grand Complications Sky Moon Tourbillon": {
      "diameters": [44],
      "bracelets": ["Leather"]
    },
    "Grand Complications Minute Repeater": {
      "diameters": [40, 42],
      "bracelets": ["Leather"]
    },
    "Grand Complications Perpetual Calendar": {
      "diameters": [39, 41],
      "bracelets": ["Leather", "Metal"]
    }
  }
};

const WATCH_MOVEMENTS = {
  "Rolex": {
    "Datejust": {
      "36": [
        { years: [1977, 1988], caliber: "Kaliber 3035", power_reserve: "42 godziny" },
        { years: [1988, 2018], caliber: "Kaliber 3135", power_reserve: "48 godzin" },
        { years: [2018, 2025], caliber: "Kaliber 3235", power_reserve: "70 godzin" }
      ],
      "41": [
        { years: [2009, 2016], caliber: "Kaliber 3136", power_reserve: "48 godzin" },
        { years: [2016, 2025], caliber: "Kaliber 3235", power_reserve: "70 godzin" }
      ]
    },
    "Submariner": {
      "40": [
        { years: [1988, 2020], caliber: "Kaliber 3135", power_reserve: "48 godzin" }
      ],
      "41": [
        { years: [2020, 2025], caliber: "Kaliber 3230/3235", power_reserve: "70 godzin" }
      ]
    },
    "GMT-Master II": {
      "40": [
        { years: [1983, 2007], caliber: "Kaliber 3085/3185", power_reserve: "50 godzin" },
        { years: [2007, 2018], caliber: "Kaliber 3186", power_reserve: "50 godzin" },
        { years: [2018, 2025], caliber: "Kaliber 3285", power_reserve: "70 godzin" }
      ]
    },
    "Explorer": {
      "36": [
        { years: [1989, 2010], caliber: "Kaliber 3130", power_reserve: "48 godzin" },
        { years: [2021, 2025], caliber: "Kaliber 3230", power_reserve: "70 godzin" }
      ],
      "39": [
        { years: [2010, 2021], caliber: "Kaliber 3132", power_reserve: "48 godzin" }
      ]
    },
    "Day-Date": {
      "36": [
        { years: [1988, 2019], caliber: "Kaliber 3155", power_reserve: "48 godzin" },
        { years: [2019, 2025], caliber: "Kaliber 3255", power_reserve: "70 godzin" }
      ],
      "40": [
        { years: [2015, 2025], caliber: "Kaliber 3255", power_reserve: "70 godzin" }
      ]
    },
    "Oyster Perpetual": {
      "34": [
        { years: [1989, 2020], caliber: "Kaliber 3130", power_reserve: "48 godzin" },
        { years: [2020, 2025], caliber: "Kaliber 2232", power_reserve: "55 godzin" }
      ],
      "36": [
        { years: [2000, 2020], caliber: "Kaliber 3130", power_reserve: "48 godzin" },
        { years: [2020, 2025], caliber: "Kaliber 3230", power_reserve: "70 godzin" }
      ],
      "41": [
        { years: [2020, 2025], caliber: "Kaliber 3230", power_reserve: "70 godzin" }
      ]
    },
    "Yacht-Master": {
      "37": [
        { years: [2015, 2025], caliber: "Kaliber 2236", power_reserve: "55 godzin" }
      ],
      "40": [
        { years: [1992, 2019], caliber: "Kaliber 3135", power_reserve: "48 godzin" },
        { years: [2019, 2025], caliber: "Kaliber 3235", power_reserve: "70 godzin" }
      ],
      "42": [
        { years: [2019, 2025], caliber: "Kaliber 3235", power_reserve: "70 godzin" }
      ]
    }
  },
  "Chopard": {
    "Happy Sport": {
      "33": [
        { years: [2021, 2025], caliber: "Kaliber 09.01-C", power_reserve: "42 godziny" }
      ]
    },
    "Mille Miglia": {
      "43": [
        { years: [2015, 2025], caliber: "Kaliber 01.08-C", power_reserve: "60 godzin" }
      ]
    },
    "L.U.C": {
      "43": [
        { years: [2025, 2025], caliber: "Kaliber 98.09-L", power_reserve: "216 godzin (9 dni)" }
      ]
    },
    "Alpine Eagle": {
      "41": [
        { years: [2019, 2025], caliber: "Kaliber 01.01-C", power_reserve: "60 godzin" }
      ]
    }
  },
  "Cartier": {
    "Tank Must": {
      "25": [
        { years: [2017, 2025], caliber: "Kaliber 1917 MC", power_reserve: "38 godzin" }
      ]
    },
    "Santos de Cartier": {
      "40": [
        { years: [2018, 2025], caliber: "Kaliber 1847 MC", power_reserve: "42 godziny" }
      ]
    },
    "Ballon Bleu": {
      "40": [
        { years: [2020, 2025], caliber: "Kaliber 1847 MC", power_reserve: "42 godziny" }
      ]
    },
    "Pasha de Cartier": {
      "41": [
        { years: [2020, 2025], caliber: "Kaliber 1847 MC", power_reserve: "42 godziny" }
      ]
    }
  }
};

const MATERIALS = [
  { value: 'steel', label: 'Stal' },
  { value: 'steel_yg', label: 'Stal + Żółte złoto' },
  { value: 'steel_rg', label: 'Stal + Różowe złoto' },
  { value: 'steel_wg', label: 'Stal + Białe złoto' },
  { value: 'platinum', label: 'Platyna' },
  { value: 'yg', label: 'Żółte złoto' },
  { value: 'wg', label: 'Białe złoto' },
  { value: 'rg', label: 'Różowe złoto' },
  { value: 'platinum_yg', label: 'Platyna + Żółte złoto' },
  { value: 'platinum_wg', label: 'Platyna + Białe złoto' },
  { value: 'platinum_rg', label: 'Platyna + Różowe złoto' }
];

const LOCATIONS = [
  { value: 'PAN', label: 'Panorama M&M GOLD' },
  { value: 'VVS', label: 'Panorama VVS' },
  { value: 'MODO', label: 'Modo M&M GOLD' },
  { value: 'KLIF', label: 'Klif M&M GOLD' },
  { value: 'GP', label: 'Galeria Północna M&M GOLD' },
  { value: 'SB', label: 'Stary Browar M&M GOLD' },
  { value: 'MB', label: 'Marbella' },
  { value: 'GPAN', label: 'Główny Panorama', admin: true },
  { value: 'GMODO', label: 'Główny Modo', admin: true }
];

const GOLD_PURITY = [
  { value: '585', label: '14K (585)' },
  { value: '750', label: '18K (750)' }
];

const JEWELRY_TYPES = [
  { value: 'ring', label: 'Pierścionek' },
  { value: 'engagement_ring', label: 'Pierścionek zaręczynowy' },
  { value: 'necklace', label: 'Naszyjnik' },
  { value: 'pendant', label: 'Zawieszka' },
  { value: 'bracelet', label: 'Bransoletka' },
  { value: 'brooch', label: 'Broszka' },
  { value: 'other', label: 'Inny' }
];

const ProductCategory = {
  WATCH: "watch",
  ENGAGEMENT_RING: "engagement_ring",
  RING: "ring",
  BRACELET: "bracelet",
  NECKLACE: "necklace",
  EARRINGS: "earrings",
  BROOCH: "brooch",
  PENDANT: "pendant"
};

const Collection = {
  MM4MEN: "mm4men",
  INVISIBLE: "invisible",
  MYSTERY: "mystery",
  HIGH_JEWELERS: "high_jewelers",
  STANDARD: "standard",
  CUSTOM: "custom"
};

const JEWELRY_COLLECTIONS = [
  { value: Collection.MM4MEN, label: 'MM4MEN' },
  { value: Collection.INVISIBLE, label: 'Invisible' },
  { value: Collection.MYSTERY, label: 'Mystery' },
  { value: Collection.HIGH_JEWELERS, label: 'High Jewelers' },
  { value: Collection.STANDARD, label: 'Standardowa' },
  { value: Collection.CUSTOM, label: 'Niestandardowa' }
];

const GOLD_COLORS = [
  { value: 'yellow', label: 'Żółte' },
  { value: 'white', label: 'Białe' },
  { value: 'rose', label: 'Różowe' },
  { value: 'two_tone', label: 'Dwukolorowe' },
  { value: 'three_tone', label: 'Trójkolorowe' }
];

const GOLD_FINENESS = [
  { value: '585', label: '585' },
  { value: '750', label: '750' },
  { value: '333', label: '333' },
  { value: 'other', label: 'Inna' }
];

const STONE_SHAPES = [
  { value: 'round', label: 'Okrągły' },
  { value: 'princess', label: 'Princess' },
  { value: 'cushion', label: 'Cushion' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'oval', label: 'Owalny' },
  { value: 'radiant', label: 'Radiant' },
  { value: 'asscher', label: 'Asscher' },
  { value: 'marquise', label: 'Markiza' },
  { value: 'pear', label: 'Gruszka' },
  { value: 'heart', label: 'Serce' },
  { value: 'baguette', label: 'Bagiet' },
  { value: 'trillion', label: 'Trillion' }
];

const DIAMOND_COLORS = [
  { value: 'D', label: 'D' },
  { value: 'E', label: 'E' },
  { value: 'F', label: 'F' },
  { value: 'G', label: 'G' },
  { value: 'H', label: 'H' },
  { value: 'I', label: 'I' },
  { value: 'J', label: 'J' },
  { value: 'K', label: 'K' },
  { value: 'L', label: 'L' },
  { value: 'M', label: 'M' },
  { value: 'N', label: 'N' }
];

const DIAMOND_CLARITY = [
  { value: 'IF', label: 'IF' },
  { value: 'VVS', label: 'VVS' },  // Dodana opcja ogólna VVS
  { value: 'VVS1', label: 'VVS1' },
  { value: 'VVS2', label: 'VVS2' },
  { value: 'VS', label: 'VS' },
  { value: 'VS1', label: 'VS1' },
  { value: 'VS2', label: 'VS2' },
  { value: 'SI', label: 'SI' },    // Dodana opcja ogólna SI
  { value: 'SI1', label: 'SI1' },
  { value: 'SI2', label: 'SI2' },
  { value: 'I1', label: 'I1' },
  { value: 'I2', label: 'I2' },
  { value: 'I3', label: 'I3' }
];

const DIAMOND_CUT = [
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Very Good', label: 'Very Good' },
  { value: 'Good', label: 'Good' },
  { value: 'Fair', label: 'Fair' },
  { value: 'Poor', label: 'Poor' }
];

const COLORED_STONE_COLORS = [
  { value: 'light', label: 'Jasny' },
  { value: 'medium', label: 'Średni' },
  { value: 'dark', label: 'Ciemny' }
];

const CERTIFICATE_TYPES = [
  { value: 'none', label: 'Brak' },
  { value: 'igi', label: 'IGI' },
  { value: 'gia', label: 'GIA' },
  { value: 'hrd', label: 'HRD' }
];

const initialProductData = {
  // Pola podstawowe
  category: '',            // required
  collection: 'standard',  // default
  sku: '',                // optional
  brand: '',              // required tylko dla zegarków
  model: '',              // required tylko dla zegarków
  material: '',           // required
  price: '',              // required
  location: '',           // required
  status: 'available',    // default
  description: '',        // optional
  images: [],            // default []
  certificate_numbers: {}, // default {}

  // Pola dla zegarków
  case_diameter: '',
  bracelet_type: '',
  movement: '',
  diamonds_setting: '',
  dial_color: '',
  reference_number: '',
  production_year: '',
  condition: '',
  has_documents: false,
  serial_number: '',

  // Pola dla biżuterii
  jewelry_type: '',
  gold_color: '',
  gold_purity: '',
  ring_size: '',
  weight_grams: '',
  clasp_type: '',

  // Parametry kamienia centralnego
  has_center_stone: false,
  center_stone_type: '',
  center_stone_shape: '',
  center_stone_size: '',
  center_stone_color: '',
  center_stone_clarity: '',
  center_stone_cut: '',
  center_stone_polish: '',
  center_stone_symmetry: '',
  center_stone_fluorescence: '',
  center_stone_has_certificate: false,
  center_stone_certificate_institute: '',
  center_stone_certificate_number: '',

  // Parametry drugiego kamienia centralnego (dla kolczyków)
  has_second_center_stone: false,
  second_center_stone_type: '',
  second_center_stone_shape: '',
  second_center_stone_size: '',
  second_center_stone_color: '',
  second_center_stone_clarity: '',
  second_center_stone_cut: '',
  second_center_stone_polish: '',
  second_center_stone_symmetry: '',
  second_center_stone_has_certificate: false,
  second_center_stone_certificate_institute: '',
  second_center_stone_certificate_number: '',

  // Parametry kamieni dodatkowych
  has_side_stones: false,
  side_stones_type: '',  
  // Ujednolicamy nazwy pól
  side_stones_count: '',  
  side_stones_weight: '', 
  side_stones_color_from: '',  
  side_stones_color_to: '',  
  side_stones_clarity_from: '', 
  side_stones_clarity_to: '', 
  side_stones_cut: '',     
  side_stones_polish: '', 
  side_stones_symmetry: '',   
  colored_stones: {}
};

const STONE_TYPES = [
  { value: 'diamond', label: 'Diament' },
  { value: 'ruby', label: 'Rubin' },
  { value: 'sapphire', label: 'Szafir' },
  { value: 'emerald', label: 'Szmaragd' },
  { value: 'tanzanite', label: 'Tanzanit' },
  { value: 'amethyst', label: 'Ametyst' },
  { value: 'topaz', label: 'Topaz' },
  { value: 'aquamarine', label: 'Akwamaryn' },
  { value: 'tourmaline', label: 'Turmalin' },
  { value: 'peridot', label: 'Perydot' },
  { value: 'garnet', label: 'Granat' },
  { value: 'opal', label: 'Opal' },
  { value: 'pearl', label: 'Perła' },
  { value: 'morganite', label: 'Morganit' },
  { value: 'citrine', label: 'Cytryn' },
  { value: 'alexandrite', label: 'Aleksandryt' }
];

const STONE_GRADE_OPTIONS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'very_good', label: 'Very Good' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

const CentralStoneForm = ({ stone, onChange, prefix = '' }) => {
  const handleChange = (field, value) => {
    onChange({ ...stone, [field]: value });
  };

  const isDiamond = stone.stone_type === 'diamond';
  const isRound = stone.shape === 'round';
  const showDiamondFields = isDiamond && stone.has_stone;
  const requireCutField = showDiamondFields && isRound;
  const requirePolishSymmetry = showDiamondFields;

  return (
    <Box sx={{ mt: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={stone.has_stone}
            onChange={(e) => handleChange('has_stone', e.target.checked)}
          />
        }
        label="Wyrób posiada kamień centralny"
      />

      {stone.has_stone && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Rodzaj kamienia</InputLabel>
              <Select
                value={stone.stone_type || ''}
                onChange={(e) => handleChange('stone_type', e.target.value)}
                label="Rodzaj kamienia"
              >
                {STONE_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Kształt kamienia</InputLabel>
              <Select
                value={stone.shape || ''}
                onChange={(e) => handleChange('shape', e.target.value)}
                label="Kształt kamienia"
              >
                {STONE_SHAPES.map(shape => (
                  <MenuItem key={shape.value} value={shape.value}>
                    {shape.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Waga (ct)"
              type="number"
              inputProps={{ step: "0.01" }}
              value={stone.weight || ''}
              onChange={(e) => handleChange('weight', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Kolor"
              value={stone.color || ''}
              onChange={(e) => handleChange('color', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Czystość"
              value={stone.clarity || ''}
              onChange={(e) => handleChange('clarity', e.target.value)}
            />
          </Grid>

          {showDiamondFields && (
            <>
              {requireCutField && (
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required>
                    <InputLabel>Szlif</InputLabel>
                    <Select
                      name="stone_cut"
                      value={stone.cut || ''}
                      onChange={(e) => handleChange('cut', e.target.value)}
                    >
                      {STONE_GRADE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Polerowanie</InputLabel>
                  <Select
                    name="stone_polish"
                    value={stone.polish || ''}
                    onChange={(e) => handleChange('polish', e.target.value)}
                  >
                    {STONE_GRADE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Symetria</InputLabel>
                  <Select
                    name="stone_symmetry"
                    value={stone.symmetry || ''}
                    onChange={(e) => handleChange('symmetry', e.target.value)}
                  >
                    {STONE_GRADE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={stone.has_certificate}
                  onChange={(e) => handleChange('has_certificate', e.target.checked)}
                />
              }
              label="Kamień posiada dodatkowy certyfikat"
            />
          </Grid>

          {stone.has_certificate && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Instytut certyfikacji</InputLabel>
                <Select
                  value={stone.certificate_institute || ''}
                  onChange={(e) => handleChange('certificate_institute', e.target.value)}
                >
                  <MenuItem value="igi">IGI</MenuItem>
                  <MenuItem value="gia">GIA</MenuItem>
                  <MenuItem value="hrd">HRD</MenuItem>
                  <MenuItem value="hrd">INNY</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {stone.has_certificate && stone.certificate_institute && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Numer certyfikatu"
                value={stone.certificate_number || ''}
                onChange={(e) => handleChange('certificate_number', e.target.value)}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

const SideStonesForm = ({ sideStones, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...sideStones, [field]: value });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={sideStones.has_stones}
            onChange={(e) => handleChange('has_stones', e.target.checked)}
          />
        }
        label="Wyrób posiada kamienie dodatkowe"
      />

      {sideStones.has_stones && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Rodzaj kamienia</InputLabel>
              <Select
                value={sideStones.stone_type || ''}
                onChange={(e) => handleChange('stone_type', e.target.value)}
                label="Rodzaj kamienia"
              >
                {STONE_TYPES.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              required
              label="Waga (ct)"
              type="number"
              inputProps={{ step: "0.01" }}
              value={sideStones.total_weight || ''}
              onChange={(e) => handleChange('total_weight', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              required
              label="Liczba kamieni"
              type="number"
              value={sideStones.side_stones_count || ''}
              onChange={(e) => handleChange('side_stones_count', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Opcjonalne parametry kamieni dodatkowych:
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Kolor"
                  value={sideStones.color || ''}
                  onChange={(e) => handleChange('color', e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Czystość"
                  value={sideStones.clarity || ''}
                  onChange={(e) => handleChange('clarity', e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Szlif</InputLabel>
                  <Select
                    value={sideStones.cut || ''}
                    onChange={(e) => handleChange('cut', e.target.value)}
                  >
                    <MenuItem value="">Wybierz szlif</MenuItem>
                    {STONE_GRADE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Polerowanie</InputLabel>
                  <Select
                    value={sideStones.polish || ''}
                    onChange={(e) => handleChange('polish', e.target.value)}
                  >
                    <MenuItem value="">Wybierz polerowanie</MenuItem>
                    {STONE_GRADE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Symetria</InputLabel>
                  <Select
                    value={sideStones.symmetry || ''}
                    onChange={(e) => handleChange('symmetry', e.target.value)}
                  >
                    <MenuItem value="">Wybierz symetrię</MenuItem>
                    {STONE_GRADE_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const AddProduct = ({ mode = 'add' }) => {
  const { id } = useParams(); // Pobieranie ID produktu z URL jeśli jesteśmy w trybie edycji
  const { currentUser, userRole } = useContext(AuthContext);
  const navigate = useNavigate();
  const [productType, setProductType] = useState(null);
  const [productData, setProductData] = useState(initialProductData);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [successDialog, setSuccessDialog] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableDiameters, setAvailableDiameters] = useState([]);
  const [availableBracelets, setAvailableBracelets] = useState([]);

  const handleProductTypeSelect = (type) => {
    setProductType(type);
  };

  const handleSuccessAction = (action) => {
    setSuccessDialog(false);
    
    // Obsługa różnych akcji
    if (action === 'similar') {
      // Pozostaw dane formularza - nie rób nic
    } else if (action === 'another') {
      // Resetuj formularz, ale pozostań na stronie
      resetForm();
    } else if (action === 'new') {
      // Resetuj formularz i wróć do listy produktów
      resetForm();
      navigate('/products');
    } else {
      // Domyślne zachowanie
      resetForm();
      navigate('/products');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getRequiredFields = (type) => {
    const commonFields = ['material', 'location', 'price'];

    if (type === 'watch') {
      // Dodane wszystkie wymagane pola dla zegarków
      return [
        ...commonFields, 
        'brand', 
        'model', 
        'case_diameter', 
        'bracelet_type', 
        'diamonds_setting', 
        'dial_color', 
        'condition'
      ];
    } else if (type === 'jewelry' || productData.category === productData.jewelry_type) {
      // Sprawdzamy czy to biżuteria albo czy kategoria jest zgodna z typem biżuterii
      return [...commonFields, 'gold_purity', 'weight_grams', 'jewelry_type'];
    } else {
      return commonFields;
    }
  };

  const validateForm = () => {
    const requiredFields = getRequiredFields(productType);
    const isWatch = productType === 'watch';

    console.log('Validating form for type:', productType, 'isWatch:', isWatch);
    console.log('Required fields:', requiredFields);
    console.log('Current data:', productData);

    const missingFields = requiredFields.filter(field => {
      const value = productData[field];
      const isEmpty = value === undefined || value === '' || value === null;

      // Ignoruj brand i model jeśli to nie zegarek
      if (!isWatch && (field === 'brand' || field === 'model')) {
        console.log(`Ignoring ${field} field because product is not a watch`);
        return false;
      }

      console.log(`Checking field ${field}: value = ${value}, isEmpty = ${isEmpty}`);
      return isEmpty;
    });

    if (missingFields.length > 0) {
      console.log('Missing Fields:', missingFields);
      console.log('Fields values:', missingFields.map(field => `${field}: ${productData[field]}`));
      setSnackbar({
        open: true,
        message: `Proszę wypełnić wszystkie wymagane pola: ${missingFields.join(', ')}`,
        severity: 'error'
      });
      return false;
    }
    return true;
  };

  const updateMovement = (brand, model, diameter, year) => {
    // Jeśli nie ma roku, nie pokazujemy błędu
    if (!brand || !model || !diameter || !year) {
      setProductData(prev => ({
        ...prev,
        movement_type: ''
      }));
      return;
    }

    const movements = WATCH_MOVEMENTS[brand]?.[model]?.[diameter.toString()];
    if (!movements) {
      setProductData(prev => ({
        ...prev,
        movement_type: ''
      }));
      return;
    }

    const yearNum = parseInt(year);
    if (isNaN(yearNum)) {
      setProductData(prev => ({
        ...prev,
        movement_type: ''
      }));
      return;
    }

    const movement = movements.find(m => {
      const [startYear, endYear] = m.years;
      return yearNum >= startYear && yearNum <= endYear;
    });

    if (movement) {
      const movementText = `${movement.caliber} – rezerwa chodu: ${movement.power_reserve}`;
      setProductData(prev => ({
        ...prev,
        movement_type: movementText
      }));
    } else {
      setProductData(prev => ({
        ...prev,
        movement_type: ''
      }));
    }
  };

  useEffect(() => {
    if (productData.brand) {
      const models = Object.keys(WATCH_BRANDS_MODELS_BRACELETS[productData.brand]);
      setAvailableModels(models);
      setAvailableDiameters([]);
      setAvailableBracelets([]);
    } else {
      setAvailableModels([]);
      setAvailableDiameters([]);
      setAvailableBracelets([]);
    }
  }, [productData.brand]);

  useEffect(() => {
    if (productData.brand && productData.model) {
      if (productData.model === 'other') {
        // Dla modelu "Inny" ustawiamy puste listy, ponieważ użytkownik wpisze własne wartości
        setAvailableDiameters([]);
        setAvailableBracelets([]);
      } else {
        // Dla standardowych modeli pobieramy dane z obiektu
        const modelData = WATCH_BRANDS_MODELS_BRACELETS[productData.brand][productData.model];
        setAvailableDiameters(modelData.diameters);
        setAvailableBracelets(modelData.bracelets);
      }
    } else {
      setAvailableDiameters([]);
      setAvailableBracelets([]);
    }
  }, [productData.brand, productData.model]);

  useEffect(() => {
    // Tylko jeśli wszystkie potrzebne pola są wypełnione
    if (productData.brand && productData.model && productData.case_diameter && productData.production_year) {
      updateMovement(
        productData.brand,
        productData.model,
        productData.case_diameter,
        productData.production_year // może być puste
      );
    }
  }, [productData.brand, productData.model, productData.case_diameter, productData.production_year]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    // Konwersja wartości numerycznych
    let processedValue = value;
    const decimalFields = ['price', 'total_weight', 'center_stone_weight', 'side_stones_weight'];
    const integerFields = ['case_diameter', 'production_year', 'side_stones_count'];

    // Jeśli pole ma wartość "other", nie konwertuj jej
    if (value !== '' && value !== 'other') {
      if (decimalFields.includes(name)) {
        processedValue = parseFloat(value) || '';
      } else if (integerFields.includes(name)) {
        processedValue = parseInt(value) || '';
      }
    }

    // Obsługa zegarka
    if (name === 'brand' && productType === 'watch') {
      setProductData(prev => ({
        ...prev,
        [name]: processedValue,
        model: '',
        case_diameter: '',
        bracelet_type: '',
        category: 'watch' // Upewniamy się że category jest ustawione
      }));
      setAvailableModels(Object.keys(WATCH_BRANDS_MODELS_BRACELETS[value] || {}));
    }
    else if (name === 'model' && productType === 'watch') {
      setProductData(prev => ({
        ...prev,
        [name]: processedValue,
        case_diameter: '',
        bracelet_type: '',
        category: 'watch' // Upewniamy się że category jest ustawione
      }));
      const brandData = WATCH_BRANDS_MODELS_BRACELETS[productData.brand]?.[value];
      setAvailableDiameters(brandData?.diameters || []);
      setAvailableBracelets(brandData?.bracelets || []);
    }
    else if (name === 'jewelry_type') {
      setProductData(prev => ({
        ...prev,
        [name]: processedValue,
        category: processedValue // Ustawiamy category na podstawie jewelry_type
      }));
    }
    else {
      setProductData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = (keepData = false) => {
    setProductType('');
    // Resetujemy dane ale ustawiamy od razu typ produktu
    setProductData({
      ...initialProductData,
      category: productType
    });
    setSelectedImages([]);
  };

  // Funkcja do sanityzacji danych przed zapisem - zamienia undefined na pusty string
  const sanitizeData = (data) => {
    const sanitized = {};

    // Iteruj przez wszystkie pola i zastąp undefined przez pusty string
    for (const key in data) {
      if (data[key] === undefined) {
        sanitized[key] = "";
      } else {
        sanitized[key] = data[key];
      }
    }

    // Dodatkowo upewnij się, że total_carat_weight ma wartość
    if (sanitized.total_carat_weight === undefined) {
      sanitized.total_carat_weight = "";
    }

    return sanitized;
  };

  // Funkcja sprawdzająca unikalność SKU
  const checkSkuUniqueness = async (sku) => {
    if (!sku || sku.trim() === '') return true; // Jeśli SKU jest puste, to uznajemy je za unikalne

    const productsRef = collection(db, 'products');
    const q = query(productsRef, where('sku', '==', sku.trim()));
    const querySnapshot = await getDocs(q);

    return querySnapshot.empty; // Zwraca true jeśli nie znaleziono produktu z takim SKU
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    if (!currentUser) {
      setSnackbar({
        open: true,
        message: 'Musisz być zalogowany aby dodać produkt',
        severity: 'error'
      });
      return;
    }

    // Sprawdź czy użytkownik ma odpowiednie uprawnienia
    if (userRole !== 'admin' && userRole !== 'marketing') {
      setSnackbar({
        open: true,
        message: 'Tylko administrator lub marketing może dodawać produkty',
        severity: 'error'
      });
      return;
    }

    // Sprawdź unikalność SKU jeśli zostało podane
    if (productData.sku && productData.sku.trim() !== '') {
      const isSkuUnique = await checkSkuUniqueness(productData.sku);
      if (!isSkuUnique) {
        setSnackbar({
          open: true,
          message: 'Produkt o podanym identyfikatorze (ID) już istnieje. Proszę użyć innego identyfikatora.',
          severity: 'error'
        });
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Debug
      console.log('User:', currentUser);
      console.log('Product type:', productType);

      // Sanityzuj dane przed wysłaniem (zamień undefined na pusty string)
      const sanitizedData = sanitizeData(productData);
    
      // Przygotuj dane do wysłania
      const dataToSend = {
        ...sanitizedData,
        category: productType === 'jewelry' ? sanitizedData.jewelry_type : productType,
        // Używamy kolekcji z formularza lub 'standard' jako wartości domyślnej
        collection: sanitizedData.collection || 'standard',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        images: [], // Tymczasowo puste, będą zaktualizowane po uploadzie
        // Zawsze ustawiamy te pola, aby zapobiec błędom undefined
        total_carat_weight: sanitizedData.total_carat_weight || "",
        // Jeśli kolor tarczy to 'other', dodajemy field display_dial_color z wartością niestandardowego koloru
        display_dial_color: sanitizedData.dial_color === 'other' ? sanitizedData.custom_dial_color : sanitizedData.dial_color
      };
    
      // Dla biżuterii ustawiamy brand i model na pusty string
      if (productType === 'jewelry') {
        dataToSend.brand = "";
        dataToSend.model = "";
      }

      // Najpierw dodajemy dokument do Firestore
      const docRef = await addDoc(collection(db, 'products'), dataToSend);
      
      // Jeśli są zdjęcia, uploadujemy je do Storage
      if (selectedImages.length > 0) {
        const imageUrls = [];
        
        for (const image of selectedImages) {
          // Sprawdź czy plik jest w formacie HEIC
          let fileToUpload = image;
          let fileName = image.name;
          
          // Jeśli to plik HEIC, konwertuj go na JPEG
          if (image.name.toLowerCase().endsWith('.heic')) {
            try {
              console.log(`Konwertowanie pliku HEIC: ${image.name}`);
              const convertedBlob = await heic2any({
                blob: image,
                toType: "image/jpeg",
                quality: 0.9
              });
              
              // Zmień nazwę pliku z .heic na .jpg
              fileName = image.name.replace(/\.heic$/i, '.jpg');
              fileToUpload = new File([convertedBlob], fileName, { type: 'image/jpeg' });
              console.log(`Plik przekonwertowany: ${fileName}`);
            } catch (error) {
              console.error(`Błąd podczas konwersji pliku HEIC ${image.name}:`, error);
              // Kontynuuj z oryginalnym plikiem w przypadku błędu
            }
          }
          
          const storageRef = ref(storage, `products/${docRef.id}/${fileName}`);
          await uploadBytes(storageRef, fileToUpload);
          const url = await getDownloadURL(storageRef);
          imageUrls.push(url);
        }
        
        // Aktualizujemy dokument o URLs zdjęć
        await updateDoc(docRef, { images: imageUrls });
      }
      
      // Dodaj log o dodaniu produktu
      await addLog({
        action: 'product_create',
        entity_type: 'product',
        entity_id: docRef.id,
        user_id: currentUser.uid,
        details: dataToSend,
        location: dataToSend.location
      });

      // Jeśli wszystko się udało, pokaż dialog sukcesu
      setSuccessDialog(true);
      setSnackbar({
        open: true,
        message: 'Produkt został dodany pomyślnie!',
        severity: 'success'
      });

    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Wystąpił błąd podczas dodawania produktu.';

      // Obsługa błędów Firebase
      if (error.code) {
        switch (error.code) {
          case 'permission-denied':
            errorMessage = 'Brak uprawnień do dodania produktu.';
            break;
          case 'storage/unauthorized':
            errorMessage = 'Brak uprawnień do dodania zdjęć.';
            break;
          default:
            errorMessage = `Błąd: ${error.message}`;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderWatchFields = () => (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ py: 2 }}>Dane zegarka</Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Marka</InputLabel>
          <Select
            name="brand"
            value={productData.brand}
            onChange={handleChange}
            label="Marka"
          >
            {Object.keys(WATCH_BRANDS_MODELS_BRACELETS).map(brand => (
              <MenuItem key={brand} value={brand}>
                {brand}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required disabled={!productData.brand}>
          <InputLabel>Model</InputLabel>
          <Select
            name="model"
            value={productData.model}
            onChange={handleChange}
            label="Model"
          >
            {availableModels.map(model => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
            <MenuItem value="other">Inny</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Własny model zegarka */}
      {productData.model === 'other' && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Własny model"
            name="custom_model"
            value={productData.custom_model || ''}
            onChange={handleChange}
            helperText="Wprowadź własną nazwę modelu"
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        {productData.model === 'other' ? (
          <TextField
            fullWidth
            required
            label="Średnica koperty (mm)"
            name="case_diameter"
            type="number"
            value={productData.case_diameter || ''}
            onChange={handleChange}
            inputProps={{ min: "24", max: "50", step: "1" }}
            helperText="Wprowadź średnicę koperty w milimetrach"
          />
        ) : (
          <FormControl fullWidth required disabled={!productData.model}>
            <InputLabel>Średnica koperty (mm)</InputLabel>
            <Select
              name="case_diameter"
              value={productData.case_diameter}
              onChange={handleChange}
            >
              {availableDiameters.map(diameter => (
                <MenuItem key={diameter} value={diameter}>
                  {diameter} mm
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        {productData.model === 'other' ? (
          <TextField
            fullWidth
            required
            label="Typ bransolety"
            name="bracelet_type"
            value={productData.bracelet_type || ''}
            onChange={handleChange}
            helperText="Wprowadź własny typ bransolety"
          />
        ) : (
          <FormControl fullWidth required disabled={!productData.model}>
            <InputLabel>Typ bransolety</InputLabel>
            <Select
              name="bracelet_type"
              value={productData.bracelet_type}
              onChange={handleChange}
              label="Typ bransolety"
            >
              {availableBracelets.map(bracelet => (
                <MenuItem key={bracelet} value={bracelet}>
                  {bracelet}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Materiał</InputLabel>
          <Select
            name="material"
            value={productData.material}
            onChange={handleChange}
            label="Materiał"
          >
            {Object.entries(productType === 'watch' ? watchMaterialMap : jewelryMaterialMap).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Diamenty</InputLabel>
          <Select
            name="diamonds_setting"
            value={productData.diamonds_setting}
            onChange={handleChange}
            label="Diamenty"
          >
            <MenuItem value="full">Full</MenuItem>
            <MenuItem value="half">Half</MenuItem>
            <MenuItem value="none">Bez diamentów</MenuItem>
            <MenuItem value="diamond_bezel">Diamentowy pierścień</MenuItem>
            <MenuItem value="diamond_dial">Diamentowa tarcza</MenuItem>
            <MenuItem value="diamond_markers">Diamentowe indeksy</MenuItem>
            <MenuItem value="diamond_bezel_dial">Diamentowy pierścień i tarcza</MenuItem>
            <MenuItem value="custom">Inny układ diamentów</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {productData.diamonds_setting === 'custom' && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Opis układu diamantów"
            name="custom_diamonds_description"
            value={productData.custom_diamonds_description || ''}
            onChange={handleChange}
            helperText="Podaj własny opis rozmieszczenia diamantów"
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Kolor tarczy</InputLabel>
          <Select
            name="dial_color"
            value={productData.dial_color}
            onChange={handleChange}
            label="Kolor tarczy"
          >
            <MenuItem value="black">Czarny</MenuItem>
            <MenuItem value="white">Biały</MenuItem>
            <MenuItem value="silver">Srebrny</MenuItem>
            <MenuItem value="gold">Złoty</MenuItem>
            <MenuItem value="blue">Niebieski</MenuItem>
            <MenuItem value="green">Zielony</MenuItem>
            <MenuItem value="other">Inny</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {productData.dial_color === 'other' && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Inny kolor tarczy"
            name="custom_dial_color"
            value={productData.custom_dial_color || ''}
            onChange={handleChange}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Referencja"
          name="reference_number"
          value={productData.reference_number || ''}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Rok produkcji"
          name="production_year"
          value={productData.production_year || ''}
          onChange={handleChange}
          inputProps={{ min: "1900", max: new Date().getFullYear() }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Stan</InputLabel>
          <Select
            name="condition"
            value={productData.condition}
            onChange={handleChange}
            label="Stan"
          >
            <MenuItem value="unworn">Nie noszony</MenuItem>
            <MenuItem value="very_good">Bardzo dobry</MenuItem>
            <MenuItem value="good">Dobry</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Numer seryjny"
          name="serial_number"
          value={productData.serial_number || ''}
          onChange={handleChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControlLabel
          control={
            <Checkbox
              checked={productData.has_documents || false}
              onChange={(e) => handleChange({
                target: {
                  name: 'has_documents',
                  value: e.target.checked
                }
              })}
            />
          }
          label="Komplet dokumentów"
        />
      </Grid>



      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>Lokalizacja</InputLabel>
          <Select
            name="location"
            value={productData.location}
            onChange={handleChange}
            label="Lokalizacja"
          >
            {LOCATIONS.map(location => (
              <MenuItem key={location.value} value={location.value}>
                {location.label}
                {location.admin && <LockIcon sx={{ ml: 1, fontSize: 'small' }} />}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          required
          type="number"
          label="Cena"
          name="price"
          value={productData.price || ''}
          onChange={handleChange}
          inputProps={{ step: "0.01", min: "0" }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Opis"
          name="description"
          value={productData.description}
          onChange={handleChange}
        />
      </Grid>
    </>
  );

  const renderJewelryFields = () => {
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Dane biżuterii
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Typ biżuterii</InputLabel>
            <Select
              name="jewelry_type"
              value={productData.jewelry_type}
              onChange={handleChange}
              label="Typ biżuterii"
            >
              <MenuItem value="ring">Pierścionek</MenuItem>
              <MenuItem value="engagement_ring">Pierścionek zaręczynowy</MenuItem>
              <MenuItem value="band">Obrączka</MenuItem>
              <MenuItem value="earrings">Kolczyki</MenuItem>
              <MenuItem value="necklace">Naszyjnik</MenuItem>
              <MenuItem value="bracelet">Bransoletka</MenuItem>
              <MenuItem value="pendant">Zawieszka</MenuItem>
              <MenuItem value="brooch">Broszka</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Nazwa biżuterii"
            name="jewelry_name"
            value={productData.jewelry_name || ''}
            onChange={handleChange}
            helperText="Wprowadź nazwę biżuterii"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Lokalizacja</InputLabel>
            <Select
              name="location"
              value={productData.location}
              onChange={handleChange}
              label="Lokalizacja"
            >
              {LOCATIONS.map((loc) => (
                (!loc.admin || currentUser?.role === 'admin' || currentUser?.role === 'marketing') && (
                  <MenuItem key={loc.value} value={loc.value}>
                    {loc.label}
                    {loc.admin && (currentUser?.role === 'admin' || currentUser?.role === 'marketing') && <LockIcon sx={{ ml: 1, fontSize: 'small' }} />}
                  </MenuItem>
                )
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required>
            <InputLabel>Próba złota</InputLabel>
            <Select
              name="gold_purity"
              value={productData.gold_purity}
              onChange={handleChange}
              label="Próba złota"
            >
              {GOLD_PURITY.map((purity) => (
                <MenuItem key={purity.value} value={purity.value}>
                  {purity.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="ID"
            name="sku"
            value={productData.sku}
            onChange={handleChange}
            helperText="Unikalny identyfikator produktu"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Cena"
            name="price"
            value={productData.price}
            onChange={handleChange}
            type="number"
            inputProps={{
              step: "0.01",
              min: "0"
            }}
            helperText="Cena w PLN"
          />
        </Grid>
        
        {/* Materiał */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Materiał</InputLabel>
            <Select
              name="material"
              value={productData.material || ''}
              onChange={handleChange}
              label="Materiał"
            >
              {Object.entries(productType === 'watch' ? watchMaterialMap : jewelryMaterialMap).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Masa (g)"
            name="weight_grams"
            value={productData.weight_grams}
            onChange={handleChange}
            helperText="Podaj masę w gramach z dwoma miejscami po kropce (np. 12.27)"
            inputProps={{
              step: "0.01",
              min: "0"
            }}
          />
        </Grid>

        {/* Dynamiczne pola dla różnych typów biżuterii */}
        {productData.jewelry_type === 'ring' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Rozmiar"
              name="ring_size"
              value={productData.ring_size}
              onChange={handleChange}
              helperText="Podaj rozmiar pierścionka"
            />
          </Grid>
        )}

        {(productData.jewelry_type === 'necklace' || productData.jewelry_type === 'pendant' || productData.jewelry_type === 'bracelet') && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Długość (cm)"
              name="length_cm"
              value={productData.length_cm}
              onChange={handleChange}
              type="number"
              inputProps={{
                step: "0.1",
                min: "0"
              }}
            />
          </Grid>
        )}

        {productData.jewelry_type === 'earrings' && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Rodzaj zapięcia</InputLabel>
              <Select
                name="clasp_type"
                value={productData.clasp_type}
                onChange={handleChange}
                label="Rodzaj zapięcia"
              >
                <MenuItem value="english">Angielskie</MenuItem>
                <MenuItem value="stud">Sztyft</MenuItem>
                <MenuItem value="omega">Omega</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Pole kolekcji */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Kolekcja</InputLabel>
            <Select
              name="collection"
              value={productData.collection}
              onChange={handleChange}
              label="Kolekcja"
            >
              <MenuItem value="">Brak</MenuItem>
              <MenuItem value="mm4men">MM4MEN</MenuItem>
              <MenuItem value="invisible">Invisible</MenuItem>
              <MenuItem value="mystery">Mystery</MenuItem>
              <MenuItem value="high_jewelers">High Jewels</MenuItem>
              <MenuItem value="standard">Standardowa</MenuItem>
              <MenuItem value="custom">Niestandardowa</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Checkbox dla kamienia centralnego */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={productData.has_center_stone}
                onChange={(e) => handleChange({
                  target: {
                    name: 'has_center_stone',
                    value: e.target.checked
                  }
                })}
              />
            }
            label="Kamień centralny"
          />
        </Grid>

        {renderCenterStoneFields()}

        {/* Checkbox dla drugiego kamienia centralnego */}
        {productData.jewelry_type === 'earrings' && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={productData.has_second_center_stone}
                  onChange={(e) => handleChange({
                    target: {
                      name: 'has_second_center_stone',
                      value: e.target.checked
                    }
                  })}
                />
              }
              label="Drugi kamień centralny"
            />
          </Grid>
        )}

        {productData.jewelry_type === 'earrings' && renderSecondCenterStoneFields()}

        {/* Kamienie dodatkowe */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={productData.has_side_stones}
                onChange={(e) => {
                  const { checked } = e.target;
                  setProductData({
                    ...productData,
                    has_side_stones: checked,
                    side_stones_type: checked ? productData.side_stones_type : '',
                    // Usuwamy referencję do stone_count i używamy tylko side_stones_count
                    total_carat_weight: productData.total_carat_weight || "",
                    side_stones_color_from: checked ? productData.side_stones_color_from : '',
                    side_stones_color_to: checked ? productData.side_stones_color_to : '',
                    side_stones_clarity_from: checked ? productData.side_stones_clarity_from : '',
                    side_stones_clarity_to: checked ? productData.side_stones_clarity_to : '',
                    side_stones_cut: checked ? productData.side_stones_cut : '',
                    side_stones_polish: checked ? productData.side_stones_polish : '',
                    side_stones_symmetry: checked ? productData.side_stones_symmetry : '',
                    side_stones_weight: checked ? productData.side_stones_weight : '',
                    side_stones_count: checked ? productData.side_stones_count : ''
                  });
                }}
              />
            }
            label="Kamienie dodatkowe"
          />
        </Grid>

        {productData.has_side_stones && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Typ kamienia</InputLabel>
                <Select
                  value={productData.side_stones_type || ''}
                  onChange={(e) => setProductData({...productData, side_stones_type: e.target.value})}
                >
                  <MenuItem value="">Wybierz typ</MenuItem>
                  {STONE_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Ilość kamieni"
                name="side_stones_count"
                value={productData.side_stones_count}
                onChange={handleChange}
                type="number"
                inputProps={{ min: "0" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Łączna masa kamieni (ct)"
                name="side_stones_weight"
                value={productData.side_stones_weight}
                onChange={handleChange}
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Masa jednego kamienia (ct)"
                  name="single_stone_size"
                  value={productData.single_stone_size}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    const count = parseFloat(productData.side_stones_count);
                    const totalWeight = parseFloat(productData.side_stones_weight);
                    if (count > 0 && totalWeight > 0) {
                      const singleSize = (totalWeight / count).toFixed(2);
                      setProductData({
                        ...productData,
                        single_stone_size: singleSize
                      });
                    }
                  }}
                  sx={{ height: '56px', whiteSpace: 'nowrap' }}
                >
                  Przelicz
                </Button>
              </Box>
            </Grid>

            {productData.side_stones_type === 'diamond' && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Kolor od</InputLabel>
                    <Select
                      name="side_stones_color_from"
                      value={productData.side_stones_color_from || ''}
                      onChange={(e) => setProductData({...productData, side_stones_color_from: e.target.value})}
                    >
                      <MenuItem value="">Wybierz kolor</MenuItem>
                      {DIAMOND_COLORS.map(color => (
                        <MenuItem key={color.value} value={color.value}>
                          {color.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Kolor do</InputLabel>
                    <Select
                      name="side_stones_color_to"
                      value={productData.side_stones_color_to || ''}
                      onChange={(e) => setProductData({...productData, side_stones_color_to: e.target.value})}
                    >
                      <MenuItem value="">Wybierz kolor</MenuItem>
                      {DIAMOND_COLORS.map(color => (
                        <MenuItem key={color.value} value={color.value}>
                          {color.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Czystość od</InputLabel>
                    <Select
                      name="side_stones_clarity_from"
                      value={productData.side_stones_clarity_from || ''}
                      onChange={(e) => setProductData({...productData, side_stones_clarity_from: e.target.value})}
                    >
                      <MenuItem value="">Wybierz czystość</MenuItem>
                      {DIAMOND_CLARITY.map(clarity => (
                        <MenuItem key={clarity.value} value={clarity.value}>
                          {clarity.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Czystość do</InputLabel>
                    <Select
                      name="side_stones_clarity_to"
                      value={productData.side_stones_clarity_to || ''}
                      onChange={(e) => setProductData({...productData, side_stones_clarity_to: e.target.value})}
                    >
                      <MenuItem value="">Wybierz czystość</MenuItem>
                      {DIAMOND_CLARITY.map(clarity => (
                        <MenuItem key={clarity.value} value={clarity.value}>
                          {clarity.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Szlif</InputLabel>
                    <Select
                      name="side_stones_cut"
                      value={productData.side_stones_cut || ''}
                      onChange={(e) => setProductData({...productData, side_stones_cut: e.target.value})}
                    >
                      <MenuItem value="">Wybierz szlif</MenuItem>
                      {STONE_GRADE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Polerowanie (opcjonalne)</InputLabel>
                    <Select
                      name="side_stones_polish"
                      value={productData.side_stones_polish || ''}
                      onChange={(e) => setProductData({...productData, side_stones_polish: e.target.value})}
                    >
                      <MenuItem value="">Wybierz polerowanie</MenuItem>
                      {STONE_GRADE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Symetria (opcjonalne)</InputLabel>
                    <Select
                      name="side_stones_symmetry"
                      value={productData.side_stones_symmetry || ''}
                      onChange={(e) => setProductData({...productData, side_stones_symmetry: e.target.value})}
                    >
                      <MenuItem value="">Wybierz symetrię</MenuItem>
                      {STONE_GRADE_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </>
        )}
        
        {/* Certyfikat */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Certyfikat</InputLabel>
            <Select
              name="certificate_type"
              value={productData.certificate_type}
              onChange={handleChange}
              label="Certyfikat"
            >
              <MenuItem value="none">Brak</MenuItem>
              <MenuItem value="gia">GIA</MenuItem>
              <MenuItem value="igi">IGI</MenuItem>
              <MenuItem value="hrd">HRD</MenuItem>
              <MenuItem value="other">Inny</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {productData.certificate_type && productData.certificate_type !== 'none' && (
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              label="Numer certyfikatu"
              name="certificate_number"
              value={productData.certificate_number}
              onChange={handleChange}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            multiline
            rows={4}
            label="Opis"
            name="description"
            value={productData.description}
            onChange={handleChange}
          />
        </Grid>
      </>
    );
  };

  const renderProductTypeSelection = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ py: 4, fontFamily: 'Cinzel, serif', textAlign: 'center' }}>
        Wybierz typ produktu
      </Typography>
      
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 3
              }
            }}
            onClick={() => handleProductTypeSelect('watch')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <WatchIcon sx={{ fontSize: 60, color: '#D4AF37', mb: 2 }} />
              <Typography variant="h5" component="div">
                Zegarek
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dodaj nowy zegarek do systemu
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: 3
              }
            }}
            onClick={() => handleProductTypeSelect('jewelry')}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <DiamondIcon sx={{ fontSize: 60, color: '#D4AF37', mb: 2 }} />
              <Typography variant="h5" component="div">
                Biżuteria
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dodaj nową biżuterię do systemu
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderMainForm = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button 
          variant="outlined"
          onClick={resetForm}
          sx={{ mr: 2 }}
        >
          Anuluj
        </Button>
        <Typography variant="h4" sx={{ fontFamily: 'Cinzel, serif' }}>
          Dodaj {productType === 'watch' ? 'Zegarek' : 'Biżuterię'}
        </Typography>
      </Box>
      
      <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <Grid container spacing={3}>
          {/* Renderowanie odpowiednich pól w zależności od typu */}
          {productType === 'watch' && renderWatchFields()}
          {productType === 'jewelry' && renderJewelryFields()}
            
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Zdjęcia produktu
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternateIcon />}
              >
                Dodaj zdjęcia
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {selectedImages.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                  }}
                >
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Product ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                    onClick={() => removeImage(index)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Button 
              type="submit" 
              variant="contained" 
              size="large"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Dodawanie...' : 'Dodaj produkt'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );

  const isJewelry = productType === 'jewelry';

  const renderCenterStoneFields = () => {
    if (!productData.has_center_stone) return null;

    return (
      <>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Typ kamienia centralnego</InputLabel>
            <Select
              value={productData.center_stone_type || ''}
              onChange={(e) => setProductData({...productData, center_stone_type: e.target.value})}
            >
              <MenuItem value="">Wybierz typ</MenuItem>
              {STONE_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Kształt kamienia</InputLabel>
            <Select
              value={productData.center_stone_shape || ''}
              onChange={(e) => setProductData({...productData, center_stone_shape: e.target.value})}
            >
              <MenuItem value="">Wybierz kształt</MenuItem>
              {STONE_SHAPES.map(shape => (
                <MenuItem key={shape.value} value={shape.value}>
                  {shape.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Masa kamienia (ct)"
            name="center_stone_size"
            value={productData.center_stone_size}
            onChange={handleChange}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
          />
        </Grid>

        {productData.center_stone_type === 'diamond' && (
          <>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Kolor</InputLabel>
                <Select
                  name="center_stone_color"
                  value={productData.center_stone_color || ''}
                  onChange={(e) => setProductData({...productData, center_stone_color: e.target.value})}
                >
                  <MenuItem value="">Wybierz kolor</MenuItem>
                  {DIAMOND_COLORS.map(color => (
                    <MenuItem key={color.value} value={color.value}>
                      {color.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Czystość</InputLabel>
                <Select
                  name="center_stone_clarity"
                  value={productData.center_stone_clarity || ''}
                  onChange={(e) => setProductData({...productData, center_stone_clarity: e.target.value})}
                >
                  <MenuItem value="">Wybierz czystość</MenuItem>
                  {DIAMOND_CLARITY.map(clarity => (
                    <MenuItem key={clarity.value} value={clarity.value}>
                      {clarity.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Szlif</InputLabel>
                <Select
                  name="center_stone_cut"
                  value={productData.center_stone_cut || ''}
                  onChange={(e) => setProductData({...productData, center_stone_cut: e.target.value})}
                >
                  <MenuItem value="">Wybierz szlif</MenuItem>
                  {STONE_GRADE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Polerowanie</InputLabel>
                <Select
                  name="center_stone_polish"
                  value={productData.center_stone_polish || ''}
                  onChange={(e) => setProductData({...productData, center_stone_polish: e.target.value})}
                >
                  <MenuItem value="">Wybierz polerowanie</MenuItem>
                  {STONE_GRADE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Symetria</InputLabel>
                <Select
                  name="center_stone_symmetry"
                  value={productData.center_stone_symmetry || ''}
                  onChange={(e) => setProductData({...productData, center_stone_symmetry: e.target.value})}
                >
                  <MenuItem value="">Wybierz symetrię</MenuItem>
                  {STONE_GRADE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={productData.center_stone_has_certificate || false}
                onChange={(e) => setProductData({...productData, center_stone_has_certificate: e.target.checked})}
              />
            }
            label="Kamień posiada dodatkowy certyfikat"
          />
        </Grid>

        {productData.center_stone_has_certificate && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Instytut certyfikacji</InputLabel>
                <Select
                  value={productData.center_stone_certificate_institute || ''}
                  onChange={(e) => setProductData({...productData, center_stone_certificate_institute: e.target.value})}
                >
                  <MenuItem value="">Wybierz instytut</MenuItem>
                  <MenuItem value="igi">IGI</MenuItem>
                  <MenuItem value="gia">GIA</MenuItem>
                  <MenuItem value="hrd">HRD</MenuItem>
                  <MenuItem value="hrd">INNY</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {productData.center_stone_certificate_institute && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Numer certyfikatu"
                  value={productData.center_stone_certificate_number || ''}
                  onChange={(e) => setProductData({...productData, center_stone_certificate_number: e.target.value})}
                />
              </Grid>
            )}
          </>
        )}
      </>
    );
  };

  const renderSecondCenterStoneFields = () => {
    if (!productData.has_second_center_stone) return null;

    return (
      <>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Typ drugiego kamienia centralnego</InputLabel>
            <Select
              value={productData.second_center_stone_type || ''}
              onChange={(e) => setProductData({...productData, second_center_stone_type: e.target.value})}
            >
              <MenuItem value="">Wybierz typ</MenuItem>
              {STONE_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Kształt drugiego kamienia</InputLabel>
            <Select
              value={productData.second_center_stone_shape || ''}
              onChange={(e) => setProductData({...productData, second_center_stone_shape: e.target.value})}
            >
              <MenuItem value="">Wybierz kształt</MenuItem>
              {STONE_SHAPES.map(shape => (
                <MenuItem key={shape.value} value={shape.value}>
                  {shape.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Masa drugiego kamienia (ct)"
            name="second_center_stone_size"
            value={productData.second_center_stone_size}
            onChange={handleChange}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
          />
        </Grid>

        {productData.second_center_stone_type === 'diamond' && (
          <>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Kolor drugiego kamienia</InputLabel>
                <Select
                  name="second_center_stone_color"
                  value={productData.second_center_stone_color || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_color: e.target.value})}
                >
                  <MenuItem value="">Wybierz kolor</MenuItem>
                  {DIAMOND_COLORS.map(color => (
                    <MenuItem key={color.value} value={color.value}>
                      {color.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Czystość drugiego kamienia</InputLabel>
                <Select
                  name="second_center_stone_clarity"
                  value={productData.second_center_stone_clarity || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_clarity: e.target.value})}
                >
                  <MenuItem value="">Wybierz czystość</MenuItem>
                  {DIAMOND_CLARITY.map(clarity => (
                    <MenuItem key={clarity.value} value={clarity.value}>
                      {clarity.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Szlif drugiego kamienia</InputLabel>
                <Select
                  name="second_center_stone_cut"
                  value={productData.second_center_stone_cut || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_cut: e.target.value})}
                >
                  <MenuItem value="">Wybierz szlif</MenuItem>
                  {STONE_GRADE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Polerowanie drugiego kamienia</InputLabel>
                <Select
                  name="second_center_stone_polish"
                  value={productData.second_center_stone_polish || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_polish: e.target.value})}
                >
                  <MenuItem value="">Wybierz polerowanie</MenuItem>
                  {STONE_GRADE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Symetria drugiego kamienia</InputLabel>
                <Select
                  name="second_center_stone_symmetry"
                  value={productData.second_center_stone_symmetry || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_symmetry: e.target.value})}
                >
                  <MenuItem value="">Wybierz symetrię</MenuItem>
                  {STONE_GRADE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
        
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={productData.second_center_stone_has_certificate || false}
                onChange={(e) => setProductData({...productData, second_center_stone_has_certificate: e.target.checked})}
              />
            }
            label="Drugi kamień posiada dodatkowy certyfikat"
          />
        </Grid>

        {productData.second_center_stone_has_certificate && (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Instytut certyfikacji drugiego kamienia</InputLabel>
                <Select
                  value={productData.second_center_stone_certificate_institute || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_certificate_institute: e.target.value})}
                >
                  <MenuItem value="">Wybierz instytut</MenuItem>
                  <MenuItem value="igi">IGI</MenuItem>
                  <MenuItem value="gia">GIA</MenuItem>
                  <MenuItem value="hrd">HRD</MenuItem>
                  <MenuItem value="hrd">INNY</MenuItem>
                  
                </Select>
              </FormControl>
            </Grid>

            {productData.second_center_stone_certificate_institute && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Numer certyfikatu drugiego kamienia"
                  value={productData.second_center_stone_certificate_number || ''}
                  onChange={(e) => setProductData({...productData, second_center_stone_certificate_number: e.target.value})}
                />
              </Grid>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        p: 3,
        height: '100%',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 64px)' // odejmujemy wysokość nagłówka
      }}
    >
      {!productType ? renderProductTypeSelection() : renderMainForm()}
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontFamily: 'Cinzel, serif' }}>
          Produkt został dodany pomyślnie!
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleSuccessAction('similar')}
                startIcon={<ContentCopyIcon />}
                sx={{ height: '100%' }}
              >
                Dodaj podobny produkt
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleSuccessAction('another')}
                startIcon={<AddCircleOutlineIcon />}
                sx={{ height: '100%' }}
              >
                Dodaj inny produkt
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleSuccessAction('new')}
                startIcon={<RestartAltIcon />}
                sx={{ height: '100%' }}
              >
                Zacznij od nowa
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddProduct;
